import { useMemo, useState } from 'react';
import { agents } from '../../data/agents';
import { daily30, hourlyToday } from '../../data/callVolume';
import { salesManagers } from '../../data/salesManagers';
import { kpiTrends } from '../../data/kpiTrends';
import { deriveStats, scaleMetrics, sumMetrics } from '../../utils/metrics';
import { buildSeries, sumSeries } from '../../utils/series';
import { sourceMetrics, sourceVolume } from '../../utils/sources';
import { buildBlandReport } from '../../utils/bland';
import { buildFloorRows, summarizeFloor } from '../../utils/floor';
import { floorCalendar } from '../../data/floorCalendar';
import type { AgentRow, AgentStats, CallSource, DateRange, Team, VoiceSource, VolumePoint } from '../../types/voip';

export type TeamFilter = Team | 'All';

export interface RankedRow extends AgentRow {
  rank: number;
}

export interface TeamSummary {
  team: Team;
  manager: string;
  members: number;
  trend: number;
  stats: AgentStats;
  series: number[];
}

const totalCalls30 = agents.reduce((s, a) => s + a.metrics.outbound + a.metrics.inbound + a.metrics.missed, 0);

// Chart volume data is a shape; scale it so totals match the full user roster
const daily30Total = daily30.reduce((s, p) => s + p.outbound + p.inbound + p.missed, 0);
const volumeScale = totalCalls30 / daily30Total;

function scaleVolume(points: VolumePoint[]): VolumePoint[] {
  return points.map((p) => ({
    label: p.label,
    outbound: Math.round(p.outbound * volumeScale),
    inbound: Math.round(p.inbound * volumeScale),
    missed: Math.round(p.missed * volumeScale),
  }));
}

function volumeFor(range: DateRange): VolumePoint[] {
  if (range === 'today') return scaleVolume(hourlyToday);
  if (range === '7d') return scaleVolume(daily30.slice(-7));
  return scaleVolume(daily30);
}

export function useCallsInsightHub() {
  const [range, setRange] = useState<DateRange>('7d');
  const [team, setTeam] = useState<TeamFilter>('All');
  const [source, setSource] = useState<CallSource>('voip');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [floorDate, setFloorDate] = useState(floorCalendar.defaultDate);

  // Human-call views always reflect a voice source; Bland has its own report
  const voiceSource: VoiceSource = source === 'bland' ? 'voip' : source;
  const baseVolume = useMemo(() => volumeFor(range), [range]);
  const volume = useMemo(() => sourceVolume(baseVolume, voiceSource), [baseVolume, voiceSource]);

  const allRows = useMemo<AgentRow[]>(
    () =>
      agents.map((agent, i) => {
        const m = agent.metrics;
        const share = (m.outbound + m.inbound + m.missed) / totalCalls30;
        return {
          agent,
          stats: deriveStats(sourceMetrics(scaleMetrics(m, range), voiceSource)),
          series: buildSeries(volume, share, agent.trend, i + 1),
        };
      }),
    [range, voiceSource, volume]
  );

  // Headline numbers for each voice source, used by the source picker
  const voiceTotals = useMemo<Record<VoiceSource, AgentStats>>(() => {
    const teamAgents = team === 'All' ? agents : agents.filter((a) => a.team === team);
    const totalFor = (s: VoiceSource) =>
      deriveStats(sumMetrics(teamAgents.map((a) => sourceMetrics(scaleMetrics(a.metrics, range), s))));
    return { voip: totalFor('voip'), five9: totalFor('five9') };
  }, [range, team]);

  const bland = useMemo(() => buildBlandReport(range, team, baseVolume), [range, team, baseVolume]);

  // Rank within the selected team so ranks stay stable while searching
  const rankedTeamRows = useMemo<RankedRow[]>(() => {
    const teamRows = team === 'All' ? allRows : allRows.filter((r) => r.agent.team === team);
    return [...teamRows]
      .sort((a, b) => b.stats.talkSeconds - a.stats.talkSeconds)
      .map((r, i) => ({ ...r, rank: i + 1 }));
  }, [allRows, team]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rankedTeamRows;
    return rankedTeamRows.filter(
      (r) => r.agent.name.toLowerCase().includes(q) || r.agent.extension.includes(q)
    );
  }, [rankedTeamRows, query]);

  const blandRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return bland.rows;
    return bland.rows.filter((r) => r.agent.name.toLowerCase().includes(q) || r.agent.extension.includes(q));
  }, [bland.rows, query]);

  const userRows = useMemo(
    () => [...rows].sort((a, b) => a.agent.name.localeCompare(b.agent.name)),
    [rows]
  );

  const totals = useMemo(
    () => deriveStats(sumMetrics(rankedTeamRows.map((r) => r.stats))),
    [rankedTeamRows]
  );

  const teamSummaries = useMemo<TeamSummary[]>(
    () =>
      salesManagers.map((m) => {
        const members = allRows.filter((r) => r.agent.team === m.team);
        return {
          team: m.team,
          manager: m.manager,
          trend: m.trend,
          members: members.length,
          stats: deriveStats(sumMetrics(members.map((r) => r.stats))),
          series: sumSeries(members.map((r) => r.series)),
        };
      }),
    [allRows]
  );

  // Five9 daily floor data: per-user rows for everyone, summary scoped to the team filter
  const floorRows = useMemo(() => buildFloorRows(floorDate, allRows), [floorDate, allRows]);
  const floorById = useMemo(() => new Map(floorRows.map((r) => [r.agent.id, r])), [floorRows]);
  const floor = useMemo(
    () => summarizeFloor(team === 'All' ? floorRows : floorRows.filter((r) => r.agent.team === team)),
    [floorRows, team]
  );

  const kpiTrend = kpiTrends[range];
  const selected = allRows.find((r) => r.agent.id === selectedId) ?? null;

  return {
    range,
    setRange,
    team,
    setTeam,
    source,
    setSource,
    voiceSource,
    query,
    setQuery,
    setSelectedId,
    volume,
    allRows,
    rankedTeamRows,
    rows,
    userRows,
    totals,
    teamSummaries,
    voiceTotals,
    bland,
    blandRows,
    floorDate,
    setFloorDate,
    floorById,
    floor,
    kpiTrend,
    selected,
    teamCount: rankedTeamRows.length,
  };
}

export type CallsInsightHubState = ReturnType<typeof useCallsInsightHub>;
